import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import { DriveIcon } from '@occc-app/design-system/icons';

import { THINKING_STATUS_TEXT } from '../../drive-insights.constants';
import { formatThinkingDuration } from '../utils/format-thinking-duration';

const fade = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
`;

const containerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const animatedIconSx = {
  fontSize: 28,
  color: 'primary.main',
  animation: `${fade} 1.4s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

const staticIconSx = {
  fontSize: 28,
  color: 'primary.main',
};

const streamingStatusTextSx = {
  fontSize: 14,
  color: 'primary.main',
  animation: `${fade} 1.4s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

const durationTextSx = {
  fontSize: 14,
  color: 'primary.main',
};

type ThinkingIndicatorProps = {
  thinkingStartedAt?: number;
  thinkingEndedAt?: number;
  /** When set while streaming, replaces the default status line (e.g. long-wait copy from the orchestrator) */
  statusText?: string | null;
};

export const ThinkingIndicator = ({
  thinkingStartedAt,
  thinkingEndedAt,
  statusText,
}: ThinkingIndicatorProps) => {
  if (thinkingStartedAt && thinkingEndedAt) {
    const label = formatThinkingDuration(thinkingStartedAt, thinkingEndedAt);

    return (
      <Box role='status' aria-label={label} sx={containerSx}>
        <DriveIcon sx={staticIconSx} />
        <Box component='span' sx={durationTextSx}>
          {label}
        </Box>
      </Box>
    );
  }

  const line = statusText?.trim() ? statusText : THINKING_STATUS_TEXT;

  return (
    <Box role='status' aria-live='polite' aria-label={line} sx={containerSx}>
      <DriveIcon sx={animatedIconSx} />
      <Box component='span' sx={streamingStatusTextSx}>
        {line}
      </Box>
    </Box>
  );
};
